import { useCallback, useLayoutEffect, useRef, useState } from 'react';

export function buildMapScrollLayout(plan, viewBox, width, height) {
  const rectangles = [viewBox, { x: 0, y: 0, width: plan?.width || 720, height: plan?.height || 720 },
    ...(plan?.nodes || []), ...(plan?.beingPins || []).map(pin => ({ ...pin, width: 0, height: 0 }))];
  const valid = rectangles.filter(rect => [rect.x, rect.y, rect.width, rect.height].every(Number.isFinite));
  const left = Math.min(...valid.map(rect => rect.x));
  const top = Math.min(...valid.map(rect => rect.y));
  const right = Math.max(...valid.map(rect => rect.x + rect.width));
  const bottom = Math.max(...valid.map(rect => rect.y + rect.height));
  const rangeX = Math.max(0, right - left - viewBox.width);
  const rangeY = Math.max(0, bottom - top - viewBox.height);
  // Bound the native scroll surface even when a world spans millions of feet.
  // Scroll positions map to world coordinates; the SVG remains viewport-sized.
  return { left, top, rangeX, rangeY, width, height,
    surfaceWidth: Math.min(1000000, width + rangeX / viewBox.width * width),
    surfaceHeight: Math.min(1000000, height + rangeY / viewBox.height * height) };
}

export default function MapScrollViewport({ frameRef, plan, viewBox, children, onPointerLeave }) {
  const [layout, setLayout] = useState(null);
  const [position, setPosition] = useState(viewBox);
  const [dragging, setDragging] = useState(false);
  const layoutRef = useRef(null), positionRef = useRef(viewBox);
  const dragRef = useRef(null), pendingScrollRef = useRef(null);
  const syncScroll = useCallback(() => {
    const current = layoutRef.current, frame = frameRef.current;
    if (!current || !frame) return;
    frame.scrollLeft = current.rangeX ? (positionRef.current.x - current.left) / current.rangeX * (current.surfaceWidth - current.width) : 0;
    frame.scrollTop = current.rangeY ? (positionRef.current.y - current.top) / current.rangeY * (current.surfaceHeight - current.height) : 0;
    // Native scroll positions can round away small movements on a huge world.
    // Keep the precise dragged position when our own scroll update comes back.
    pendingScrollRef.current = { left: frame.scrollLeft, top: frame.scrollTop };
  }, [frameRef]);
  useLayoutEffect(() => {
    const frame = frameRef.current;
    setDragging(false);
    positionRef.current = viewBox;
    setPosition(viewBox);
    const measure = () => {
      if (!frame.clientWidth || !frame.clientHeight) return;
      const next = buildMapScrollLayout(plan, viewBox, frame.clientWidth, frame.clientHeight);
      layoutRef.current = next;
      setLayout(next);
    };
    const resize = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    resize?.observe(frame);
    window.addEventListener('resize', measure);
    measure();
    return () => {
      resize?.disconnect(); window.removeEventListener('resize', measure);
      const drag = dragRef.current;
      dragRef.current = null;
      if (drag?.target.hasPointerCapture?.(drag.id)) drag.target.releasePointerCapture(drag.id);
    };
  }, [frameRef, plan, viewBox]);
  useLayoutEffect(() => {
    if (!layout) return;
    syncScroll();
  }, [layout, syncScroll]);
  const scroll = () => {
    const current = layoutRef.current;
    if (!current) return;
    const frame = frameRef.current;
    const pending = pendingScrollRef.current;
    if (pending && frame.scrollLeft === pending.left && frame.scrollTop === pending.top) return;
    pendingScrollRef.current = null;
    if (dragRef.current) return;
    const next = { ...viewBox,
      x: current.left + (current.surfaceWidth > current.width ? frame.scrollLeft / (current.surfaceWidth - current.width) * current.rangeX : 0),
      y: current.top + (current.surfaceHeight > current.height ? frame.scrollTop / (current.surfaceHeight - current.height) * current.rangeY : 0) };
    positionRef.current = next;
    setPosition(next);
    onPointerLeave();
  };
  const startDrag = event => {
    if (event.button !== 0 || event.isPrimary === false || dragRef.current || !layoutRef.current) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = { id: event.pointerId, target: event.currentTarget,
      x: event.clientX, y: event.clientY, position: positionRef.current };
    setDragging(true);
    onPointerLeave();
  };
  const moveDrag = event => {
    const drag = dragRef.current, current = layoutRef.current;
    if (!drag || event.pointerId !== drag.id || !current) return;
    event.preventDefault(); event.stopPropagation();
    const scale = Math.min(current.width / drag.position.width, current.height / drag.position.height);
    const next = { ...drag.position,
      x: Math.max(current.left, Math.min(current.left + current.rangeX, drag.position.x - (event.clientX - drag.x) / scale)),
      y: Math.max(current.top, Math.min(current.top + current.rangeY, drag.position.y - (event.clientY - drag.y) / scale)) };
    positionRef.current = next;
    setPosition(next);
    syncScroll();
    onPointerLeave();
  };
  const endDrag = event => {
    const drag = dragRef.current;
    if (!drag || event.pointerId !== drag.id) return;
    dragRef.current = null;
    setDragging(false);
    if (drag.target.hasPointerCapture(drag.id)) drag.target.releasePointerCapture(drag.id);
  };
  return <div className="play2-map-frame" ref={frameRef} onScroll={scroll} onPointerLeave={onPointerLeave}
    tabIndex={0} role="region" aria-label="Scrollable map">
    <div className="play2-map-scroll-surface" style={layout ? { width: layout.surfaceWidth, height: layout.surfaceHeight } : undefined}>
      <div className={`play2-map-scroll-viewport${dragging ? ' is-dragging' : ''}`}
        onPointerDown={startDrag} onPointerMoveCapture={moveDrag} onPointerUp={endDrag}
        onPointerCancel={endDrag} onLostPointerCapture={endDrag} onDragStart={event => event.preventDefault()}
        style={layout ? { width: layout.width, height: layout.height } : undefined}>
        {children(position)}
      </div>
    </div>
  </div>;
}
